import { Button } from '@common/components/Button';
import { Flex } from '@common/components/Flex';
import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { DropdownOptionsProps, useDropdownOptionsController } from './dropdown-options.controller';
import { TextField } from '@modules/fhq-design-tool/components/questionnaire-management/components/text-field';
import { Switch } from '@common/components/switch';
import { getTestAttributes } from '@common/utils/tests';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import Dropdown from '@common/containers/fields/Dropdown';
import cx from 'classnames';
import { Hint } from '@common/components/hint';
import { QuestionnaireFieldOptionListLocalDto } from '@modules/fhq-design-tool/dto/field-option';
import { DraggableList, DraggableListProps } from '@common/components/draggable-list';

import './dropdown-options.styles.scss';

export const DropdownOptions: React.FC<DropdownOptionsProps> = (props) => {
  const {
    handleRowClick,
    selectOptionId,
    handleInsertOption,
    handleAddOption,
    handleDelete,
    handleDisplayValueChange,
    handleDisplayChange,
    handleDropdownValueChange,
  } = useDropdownOptionsController(props);
  const { data, className, possibleOptionsValue, disabled, onChange } = props;

  const [order, setOrder] = React.useState<string>("");
  const [sortedData, setSortedData] = React.useState<QuestionnaireFieldOptionListLocalDto[]>(data);

  React.useEffect(() => {
    setSortedData(data);
  }, [data]);

  // Sort function
  const sortByDisplayValue = (data: any, order = "asc")=> {
    return [...data].sort((a, b) => {
      const valA = a.displayValue?.trim().toLowerCase() || '';
      const valB = b.displayValue?.trim().toLowerCase() || '';

      const isAEmpty = !valA;
      const isBEmpty = !valB;

      if (isAEmpty && !isBEmpty) return 1;  // a is empty, push it down
      if (!isAEmpty && isBEmpty) return -1; // b is empty, push it down
      if (isAEmpty && isBEmpty) return 0;   // both empty

      if (valA < valB) return order === "asc" ? -1 : 1;
      if (valA > valB) return order === "asc" ? 1 : -1;
      return 0;
    });
  }

  const toggleOrder = (newOrder: string) => {
    setOrder(newOrder);
    const sorted = sortByDisplayValue([...data], newOrder);
    setSortedData(sorted);
  };

  const renderDeleteBtn = React.useCallback((opt: QuestionnaireFieldOptionListLocalDto, rowIndex: number) => {
    if (opt.canBeDeleted) {
      return (
        <Button
          variant="text"
          className="FhqDesignToolQuestionnaireDesignerFieldOptions__header__action" 
          onClick={handleDelete}
          data-id={opt.id}
          {...getTestAttributes(MODULE_NAME, 'delete-option', 'btn', rowIndex)}
        >
          <i className="fa fa-trash" />
        </Button>
      );
    }

    return (
      <Hint description={I18n.t('Option cannot be deleted')} />
    );
  }, [handleDelete]);

  const renderDisplayValueInput = React.useCallback((opt: QuestionnaireFieldOptionListLocalDto, rowIndex: number) => {
    return (
      <TextField
        margin="0"
        inputWidth="100%"
        metadata={{
          description: 'display-value',
          id: rowIndex,
        }}
        value={opt.displayValue?.toString() ?? ''}
        onChange={(val) => handleDisplayValueChange(opt, val)}
      />
    );
  }, [handleDisplayValueChange]);

  const renderDisplaySwitch = React.useCallback((opt: QuestionnaireFieldOptionListLocalDto, rowIndex: number) => {
    return (
      <Switch
        {...getTestAttributes(MODULE_NAME, 'display-in-fhq', 'switch', rowIndex)}
        data-id={opt.id}
        checked={opt.displayInFHQ}
        onChange={handleDisplayChange}
      />
    );
  }, [handleDisplayChange]);

  const renderDropdownValue = React.useCallback((opt: QuestionnaireFieldOptionListLocalDto, rowIndex: number) => {
    if (opt.canBeDeleted) {
      return (
        <Dropdown
          {...getTestAttributes(MODULE_NAME, 'dd-value', 'select', rowIndex)}
          selectedValue={opt.dropdownValue}
          searchable={false}
          options={possibleOptionsValue.map((x) => ({ label: x, value: x }))}
          onValueChanged={(val: string) => handleDropdownValueChange(opt, val)}
        />
      );
    }

    return <Flex>{opt.dropdownValue} <Hint description={I18n.t('Value cannot be changed')} /></Flex>
  }, [handleDropdownValueChange, possibleOptionsValue]);

  const columns = React.useMemo(() => {
    return [
      {
        width: '35%',
        id: 'drop-val',
        header: I18n.t('Dropdown Value'),
        render: renderDropdownValue,
      },
      {
        width: '35%',
        id: 'fhq-display-val',
        header: I18n.t('FHQ Display Value'),
        render: renderDisplayValueInput,
      },
      {
        width: '20%',
        id: 'display',
        header: I18n.t('Display in FHQ'),
        render: renderDisplaySwitch,
      },
      {
        width: '10%',
        id: 'actions',
        header: I18n.t('Delete'),
        render: renderDeleteBtn,
      },
    ];
  }, [renderDropdownValue, renderDisplayValueInput, renderDisplaySwitch, renderDeleteBtn]);

  const actions = React.useMemo(() => [
    {
      label: I18n.t('Add Row'),
      onClick: handleAddOption,
    },
    {
      label: I18n.t('Ins Row'),
      onClick: handleInsertOption
    },
  ], [handleAddOption, handleInsertOption]);

  const renderItem = React.useCallback((option: QuestionnaireFieldOptionListLocalDto) => {
    return (
      <Flex
        onClick={handleRowClick}
        data-id={option.id}
        className={cx("FhqDesignToolQuestionnaireDesignerFieldOptions__row", {
          "FhqDesignToolQuestionnaireDesignerFieldOptions__row--selected":
            selectOptionId?.toString() === option.id.toString(),
        })}
        key={option.id}
      >
        {columns.map((column, rowIndex) => (
          <span
            style={{ width: column.width }}
            key={column.id}
            className="FhqDesignToolQuestionnaireDesignerFieldOptions__row__cell"
          >
            {column.render(option, rowIndex)}
          </span>
        ))}
      </Flex>
    );
  }, [handleRowClick, columns, selectOptionId]);

  return (
    <Flex alignItems="flex-start" className={cx("FhqDesignToolQuestionnaireDesignerFieldOptions", { 'FhqDesignToolQuestionnaireDesignerFieldOptions--disabled': disabled },  className)}>
      <div className="FhqDesignToolQuestionnaireDesignerFieldOptions__table">
        <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerFieldOptions__header">
          {columns.map((x) => {
            return (
            <span
              style={{ width: x.width, display: "flex", justifyContent:"space-between" }}
              key={x.id}
              className="FhqDesignToolQuestionnaireDesignerFieldOptions__header__cell"
            >
              {x.header} {x.id === "fhq-display-val" ? <i style={{cursor: 'pointer'}} className={order === "desc" || order === "" ? "fa fa-arrow-up" : "fa fa-arrow-down"} onClick={() => toggleOrder(order === "" || order === "desc" ? 'asc' : "desc" )} />  : null}
            </span>
          )})}
        </Flex>
        <div>
        <DraggableList
          items={sortedData}
          renderItem={renderItem as unknown as DraggableListProps['renderItem']}
          onChange={onChange as DraggableListProps['onChange']}
        />
        </div>
      </div>
      <div>
        {actions.map((action) => (
          <Button 
            key={action.label} 
            className="FhqDesignToolQuestionnaireDesignerFieldOptions__action" 
            onClick={action.onClick}
            bsStyle="primary"
          >
            {action.label}
          </Button>
        ))}
      </div>
    </Flex>
  );
};